import { SkeletonLoader } from '@toggle/design-system';
import { ThemeExposure } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useDocumentSections } from '~/react-query-hooks/documents/useDocumentsEntity';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { CitationsAccordionWidget } from '~/widgets/citations-accordion-widget/CitationsAccordionWidget';

import sharedStyles from '../../ThemeExposureTab.module.scss';

interface SourceEvidenceSectionProps {
  themeData: ThemeExposure;
}

export const SourceEvidenceSection = ({
  themeData,
}: SourceEvidenceSectionProps) => {
  const { t } = useTranslation(['widget']);
  const { evidence, source_evidence: sourceEvidence } = themeData.relationship;

  const { data, isFetching, isError } = useDocumentSections(evidence ?? []);

  const getContent = () => {
    if (isFetching) {
      return (
        <SkeletonLoader
          areas={[
            { styles: { width: '100%', height: '180px', borderRadius: '8px' } },
          ]}
          data-testid="loading-skeleton"
        />
      );
    } else {
      return (
        <>
          {sourceEvidence && (
            <div className={sharedStyles.bodySmall} data-testid="evidence">
              {sourceEvidence}
            </div>
          )}
          <CitationsAccordionWidget
            citations={data}
            trackingMetadata={{
              source: POSTHOG_TRACKING_SOURCES.THEME_TO_COMPANY_DRAWER,
            }}
          />
        </>
      );
    }
  };

  if (isError) {
    throw new Error('SourceEvidenceSection error');
  }

  if (!sourceEvidence && !data.length) {
    return null;
  }

  return (
    <div className={sharedStyles.sectionRow}>
      <div className={sharedStyles.titleBase}>
        {t(
          'widget:assetOverview.knowledgeGraph.themeExposure.tabs.themeExposure.sourceEvidence'
        )}
      </div>
      {getContent()}
    </div>
  );
};
